/**
 * JavaScript Executor Strategy
 * Executes custom JavaScript code for calculated fields
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BaseDataFetchStrategy } from './data-fetch-strategy.interface';
import { IDataFetchContext } from '../models';
import { DataFetchType, FORM_CONFIG } from '../constants';

@Injectable({ providedIn: 'root' })
export class JSExecutorStrategy extends BaseDataFetchStrategy {
  canHandle(dataFetchType: string): boolean {
    return dataFetchType?.toLowerCase() === DataFetchType.JSExecutor;
  }
  
  getName(): string {
    return 'JavaScript Executor Strategy';
  }
  
  override validate(context: IDataFetchContext): boolean {
    if (!super.validate(context)) {
      return false;
    }
    
    if (!context.field.dataFetchFrom) {
      this.logWarning('No JavaScript code specified in dataFetchFrom', context);
      return false;
    }
    
    // Basic safety check
    if (!this.isSafeCode(context.field.dataFetchFrom)) {
      this.logWarning('JavaScript code contains potentially dangerous patterns', context);
      return false;
    }
    
    return true;
  }
  
  execute(context: IDataFetchContext): Observable<any> {
    if (!this.validate(context)) {
      return of(null);
    }
    
    try {
      // Build form values object
      const formValues = this.buildFormContext(context);
      
      // Build formData object with full selected items
      const formData = this.buildFormDataContext(context);
      
      // Get the JavaScript code
      const jsCode = context.field.dataFetchFrom!;
      
      // Execute in sandbox
      const result = this.executeInSandbox(jsCode, formValues, formData);
      
      this.logInfo(`Executed JavaScript, result: ${result}`, context);
      
      return of(result);
      
    } catch (error) {
      this.logError(error, context);
      return of(null);
    }
  }
  
  setup(context: IDataFetchContext): void {
    if (!this.validate(context)) {
      return;
    }
    
    // Extract field references from JS code
    const dependentFields = this.extractFieldReferences(context.field.dataFetchFrom!);
    
    if (dependentFields.length === 0) {
      this.logInfo('No dependent fields found in JavaScript code', context);
      return;
    }
    
    this.logInfo(`Found dependencies: ${dependentFields.join(', ')}`, context);
    
    // Subscribe to each dependent field
    dependentFields.forEach(fieldName => {
      const control = context.formGroup.get(fieldName);
      if (!control) {
        this.logWarning(`Dependent field "${fieldName}" not found in form`, context);
        return;
      }
      
      control.valueChanges.subscribe(() => {
        // Re-execute JavaScript
        this.execute(context).subscribe(result => {
          if (context.field.dbFeildName) {
            const targetControl = context.formGroup.get(context.field.dbFeildName);
            targetControl?.setValue(result, { emitEvent: false });
          }
        });
      });
    });
  }
  
  /**
   * Build context object with all form values
   */
  private buildFormContext(context: IDataFetchContext): Record<string, any> {
    const formValues: Record<string, any> = {};
    
    context.allFields?.forEach(field => {
      if (field.dbFeildName) {
        formValues[field.dbFeildName] = context.formGroup.get(field.dbFeildName)?.value;
      }
    });
    
    return formValues;
  }

  /**
   * Build formData object with full selected item objects for dropdowns
   */
  private buildFormDataContext(context: IDataFetchContext): Record<string, any> {
    const formData: Record<string, any> = {};
    
    context.allFields?.forEach(field => {
      if (field.dbFeildName && (field.uiInputType === 'select' || field.uiInputType === 'selectnadd')) {
        const value = context.formGroup.get(field.dbFeildName)?.value;
        
        if (value && field.data && Array.isArray(field.data)) {
          // Find the selected item from the field's data array
          const idProperty = field.patchPropertyName || 'id';
          const selectedItem = field.data.find(item => {
            return item[idProperty] === value;
          });
          
          if (selectedItem) {
            formData[field.dbFeildName] = selectedItem;
          }
        }
      }
    });
    
    return formData;
  }
  
  /**
   * Execute JavaScript code in a sandboxed environment
   */
  private executeInSandbox(code: string, formValues: Record<string, any>, formData: Record<string, any>): any {
    // Create safe execution context
    const jsContext = {
      form: formValues,
      formData: formData,
      Math: Math,
      Date: Date,
      parseFloat: parseFloat,
      parseInt: parseInt,
      Number: Number,
      String: String,
      Boolean: Boolean,
      console: {
        log: (...args: any[]) => {
          if (FORM_CONFIG.JS_ENABLE_CONSOLE) {
            console.log('[JSExecutor]', ...args);
          }
        },
        warn: (...args: any[]) => {
          if (FORM_CONFIG.JS_ENABLE_CONSOLE) {
            console.warn('[JSExecutor]', ...args);
          }
        },
        error: (...args: any[]) => {
          if (FORM_CONFIG.JS_ENABLE_CONSOLE) {
            console.error('[JSExecutor]', ...args);
          }
        },
      }
    };
    
    const paramNames = Object.keys(jsContext);
    const paramValues = Object.values(jsContext);
    
    // Create function in strict mode
    const func = new Function(...paramNames, `
      'use strict';
      try {
        return (function() {
          ${code}
        })();
      } catch (error) {
        console.error('[JSExecutor] Runtime Error:', error);
        return null;
      }
    `);
    
    // Execute with timeout protection
    return func(...paramValues);
  }
  
  /**
   * Extract field references from JavaScript code
   * Matches: form.fieldName, form['fieldName'], formData.fieldName, formData['fieldName']
   */
  private extractFieldReferences(code: string): string[] {
    const fieldNames = new Set<string>();
    
    // Match: form.fieldName
    const dotNotation = /form\.(\w+)/g;
    let match;
    while ((match = dotNotation.exec(code)) !== null) {
      fieldNames.add(match[1]);
    }
    
    // Match: form['fieldName'] or form["fieldName"]
    const bracketNotation = /form\[['"](\w+)['"]\]/g;
    while ((match = bracketNotation.exec(code)) !== null) {
      fieldNames.add(match[1]);
    }
    
    // Match: formData.fieldName
    const formDataDotNotation = /formData\.(\w+)/g;
    while ((match = formDataDotNotation.exec(code)) !== null) {
      fieldNames.add(match[1]);
    }
    
    // Match: formData['fieldName'] or formData["fieldName"]
    const formDataBracketNotation = /formData\[['"](\w+)['"]\]/g;
    while ((match = formDataBracketNotation.exec(code)) !== null) {
      fieldNames.add(match[1]);
    }
    
    return Array.from(fieldNames);
  }
  
  /**
   * Basic safety check for JavaScript code
   */
  private isSafeCode(code: string): boolean {
    const dangerousPatterns = [
      /eval\s*\(/gi,
      /Function\s*\(/gi,
      /setTimeout\s*\(/gi,
      /setInterval\s*\(/gi,
      /setImmediate\s*\(/gi,
      /require\s*\(/gi,
      /import\s*\(/gi,
      /export\s+/gi,
      /__proto__/gi,
      /constructor\s*\[/gi,
      /document\./gi,
      /window\./gi,
      /global\./gi,
    ];
    
    return !dangerousPatterns.some(pattern => pattern.test(code));
  }
}
